import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import {
  ComplianceChecklist,
  EditAssignedAssetRequirementRow,
  GetChecklistsRequest
} from '../../../../../../models/compliances.model';
import { CompliancesLibraryService } from '../../../../../../services/compliances-library.service';
import { ErrorHandlerService } from '../../../../../../../../../_shared/services/error-handler.service';
import { ChecklistTableComponent } from '../../../../../../../_shared/components/checklist-table/checklist-table.component';
import { EmptyStateComponent } from '../../../../../../../../../_shared/components/states/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../../../../../../../_shared/components/states/error-state/error-state.component';
import {
  ChecklistTableGroup,
  ChecklistTableSectionConfig,
  ChecklistTableToggleEvent
} from '../../../../../../../_shared/components/checklist-table/checklist-table.model';
import { CHECKLIST_TYPE_NAME_KEYS, CHECKLIST_TYPES, ChecklistTypeIdEnum } from '../../../../../../../_shared/models/checklist-type.model';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-edit-assigned-asset-requirements',
  standalone: true,
  imports: [CommonModule, RouterModule, ChecklistTableComponent, EmptyStateComponent, ErrorStateComponent, TranslocoDirective],
  templateUrl: './edit-assigned-asset-requirements.component.html',
  styleUrl: './edit-assigned-asset-requirements.component.scss'
})
export class EditAssignedAssetRequirementsComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private complianceService = inject(CompliancesLibraryService);
  private errorService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);
  private readonly cancellation = new Subject<void>();
  private toggleCancellations: Partial<Record<number, Subject<void>>> = {};

  assetId: number | null = null;
  complianceId: number | null = null;
  assetName = '';
  complianceName = '';
  complianceType = '';

  checklistGroups: ChecklistTableGroup<EditAssignedAssetRequirementRow>[] = [];
  isLoading = true;
  hasError = false;

  get isEmpty(): boolean {
    return this.checklistGroups.length === 0
      || this.checklistGroups.every(g => g.rows.length === 0);
  }

  get checklistSections(): ChecklistTableSectionConfig[] {
    return [
      this.buildSection(ChecklistTypeIdEnum.Document),
      this.buildSection(ChecklistTypeIdEnum.Inspection)
    ];
  }

  /** Safe query params for back navigation to compliance details (Assigned Assets tab). */
  get backQueryParams(): Record<string, string | number> {
    return {
      complianceId: this.complianceId ?? 0,
      complianceName: this.complianceName,
      complianceType: this.complianceType,
      tab: 'assignedAssets'
    };
  }

  ngOnInit(): void {
    this.assetId = this.getNumberQueryParam('assetId');
    this.complianceId = this.getNumberQueryParam('complianceId');
    this.assetName = this.route.snapshot.queryParamMap.get('assetName') || '';
    this.complianceName = this.route.snapshot.queryParamMap.get('complianceName') || '';
    this.complianceType = this.route.snapshot.queryParamMap.get('complianceType') || '';

    if (!this.assetId || !this.complianceId) {
      this.hasError = true;
      this.isLoading = false;
      return;
    }

    this.loadChecklistsAsync();
  }

  ngOnDestroy(): void {
    this.cancellation.next();
    this.cancellation.complete();
    Object.values(this.toggleCancellations).forEach(s => {
      s!.next();
      s!.complete();
    });
  }

  async onToggleEnabledAsync(event: ChecklistTableToggleEvent<EditAssignedAssetRequirementRow>): Promise<void> {
    const requirement = event.row;
    this.toggleCancellations[requirement.id]?.next();

    const rowCancellation = new Subject<void>();
    this.toggleCancellations[requirement.id] = rowCancellation;

    try {
      await this.complianceService.toggleRequirementEnabledAsync(
        requirement.id,
        this.complianceId!,
        this.assetId!,
        requirement.enabled,
        rowCancellation
      );
    } catch (error) {
      requirement.enabled = !requirement.enabled;
      this.errorService.handleError(error, {
        showToast: true
      });
    }
  }

  private async loadChecklistsAsync(): Promise<void> {
    try {
      this.isLoading = true;
      this.hasError = false;
      const request: GetChecklistsRequest = {
        complianceId: this.complianceId!,
        assetId: this.assetId!,
        isArchived: false
      };
      const response = await this.complianceService.getChecklistsIncludingDisabledAsync(request, this.cancellation);
      const checklists = response.data ?? [];
      this.checklistGroups = this.mapChecklistGroups(checklists);
      this.isLoading = false;
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  private mapChecklistGroups(checklists: ComplianceChecklist[]): ChecklistTableGroup<EditAssignedAssetRequirementRow>[] {
    return checklists.map(checklist => ({
      id: checklist.id,
      name: checklist.name,
      type: checklist.checklistTypeId,
      rows: checklist.requirements.map(r => ({
        id: r.id,
        name: r.name,
        enabled: r.isEnabled
      }))
    }));
  }

  private buildSection(typeId: ChecklistTypeIdEnum): ChecklistTableSectionConfig {
    const checklistType = CHECKLIST_TYPES.find(t => t.id === typeId)!;
    return {
      type: checklistType.id,
      typeAliases: [checklistType.name],
      title: this.transloco.translate('compliance.library.editAssignedRequirements.sectionTitle', {
        type: this.transloco.translate(CHECKLIST_TYPE_NAME_KEYS[checklistType.id])
      }),
      columns: [
        { header: this.transloco.translate('compliance.library.editAssignedRequirements.columns.requirement'), value: 'name', width: '4fr' },
        {
          header: this.transloco.translate('compliance.library.editAssignedRequirements.columns.action'),
          value: 'enabled',
          width: '1fr',
          type: 'toggle',
          align: 'center'
        }
      ]
    };
  }

  private getNumberQueryParam(key: string): number | null {
    const value = Number(this.route.snapshot.queryParamMap.get(key));
    return Number.isFinite(value) && value > 0 ? value : null;
  }
}